import React, { useEffect, useState } from 'react';
import useAdminAutoRefresh from './useAdminAutoRefresh';
import './AdminDashboard.css';
import './AdminUsers.css';
import './AdminOps.css';

const AUTO_REFRESH_INTERVAL_MS = 30000;

function formatDateTime(value) {
  if (!value) {
    return 'Not available';
  }

  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return 'Not available';
  }

  return date.toLocaleString([], {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function internalStatusMeta(status) {
  switch (status) {
    case 'healthy':
      return {
        emoji: 'OK',
        label: 'Healthy',
        className: 'cq-admin-ops-card cq-admin-ops-card--healthy',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--healthy',
      };
    case 'failing':
      return {
        emoji: 'ERR',
        label: 'Failing',
        className: 'cq-admin-ops-card cq-admin-ops-card--failing',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--failing',
      };
    case 'degraded':
      return {
        emoji: 'WARN',
        label: 'Degraded',
        className: 'cq-admin-ops-card cq-admin-ops-card--degraded',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--degraded',
      };
    default:
      return {
        emoji: 'UNK',
        label: 'Unknown',
        className: 'cq-admin-ops-card cq-admin-ops-card--unknown',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--unknown',
      };
  }
}

function platformStatusMeta(status) {
  switch (status) {
    case 'operational':
      return {
        emoji: 'OK',
        label: 'Operational',
        className: 'cq-admin-ops-card cq-admin-ops-card--healthy',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--healthy',
      };
    case 'outage':
      return {
        emoji: 'ERR',
        label: 'Outage',
        className: 'cq-admin-ops-card cq-admin-ops-card--failing',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--failing',
      };
    case 'degraded':
      return {
        emoji: 'WARN',
        label: 'Degraded',
        className: 'cq-admin-ops-card cq-admin-ops-card--degraded',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--degraded',
      };
    case 'manual_check':
      return {
        emoji: 'WARN',
        label: 'Manual Check',
        className: 'cq-admin-ops-card cq-admin-ops-card--degraded',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--degraded',
      };
    default:
      return {
        emoji: 'UNK',
        label: 'Unknown',
        className: 'cq-admin-ops-card cq-admin-ops-card--unknown',
        pillClassName: 'cq-admin-ops-card__pill cq-admin-ops-card__pill--unknown',
      };
  }
}

const InternalStatusCard = ({ item }) => {
  const meta = internalStatusMeta(item.status);

  return (
    <article className={meta.className}>
      <div className="cq-admin-ops-card__header">
        <p className="cq-admin-ops-card__title">{item.name}</p>
        <span className={meta.pillClassName}>{meta.emoji} {meta.label}</span>
      </div>
      <dl className="cq-admin-ops-card__details">
        <div><dt>Last Success</dt><dd>{formatDateTime(item.last_success)}</dd></div>
        <div><dt>Last Failure</dt><dd>{formatDateTime(item.last_failure)}</dd></div>
      </dl>
      {item.likely_external_issue ? (
        <p className="cq-admin-ops-card__hint">Likely external issue</p>
      ) : null}
      {item.note ? (
        <p className="cq-admin-ops-card__note">{item.note}</p>
      ) : null}
    </article>
  );
};

const PlatformStatusCard = ({ name, item }) => {
  const meta = platformStatusMeta(item.status);

  return (
    <article className={meta.className}>
      <div className="cq-admin-ops-card__header">
        <p className="cq-admin-ops-card__title">{name}</p>
        <span className={meta.pillClassName}>{meta.emoji} {meta.label}</span>
      </div>
      <p className="cq-admin-ops-card__message">{item.message || 'Status unavailable'}</p>
      <dl className="cq-admin-ops-card__details">
        <div><dt>Last Updated</dt><dd>{formatDateTime(item.updated_at)}</dd></div>
      </dl>
      {item.url ? (
        <a className="cq-admin-card-link" href={item.url} target="_blank" rel="noreferrer">View Status Page</a>
      ) : null}
    </article>
  );
};

const AdminOpsPage = () => {
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    error: '',
    data: null,
    lastUpdatedAt: null,
  });
  const { refreshNonce, triggerRefresh } = useAdminAutoRefresh({ intervalMs: AUTO_REFRESH_INTERVAL_MS });

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadOpsStatus = async ({ background = false } = {}) => {
      setState((current) => ({
        ...current,
        loading: background ? current.loading : true,
        refreshing: background,
        error: '',
      }));

      try {
        const response = await fetch('/.netlify/functions/admin-get-ops-status', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load system status.');
        }

        setState({
          loading: false,
          refreshing: false,
          error: '',
          data,
          lastUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setState({
          loading: false,
          refreshing: false,
          error: error.message || 'Unable to load system status.',
          data: null,
          lastUpdatedAt: null,
        });
      }
    };

    loadOpsStatus({ background: refreshNonce > 0 });

    return () => controller.abort();
  }, [refreshNonce]);

  const internalEntries = Object.values(state.data?.internal || {});
  const platformEntries = Object.entries(state.data?.platforms || {});

  return (
    <section className="cq-admin-page cq-admin-ops-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Operational Control Panel</p>
          <h2 className="cq-admin-page__title">Ops Status</h2>
          <p className="cq-admin-page__copy">Are our systems working right now, and if not, is it us or a provider?</p>
        </div>
        <div className="cq-admin-page__actions">
          <div className="cq-admin-page__actions-row">
            <button
              type="button"
              className="cq-admin-action-button cq-admin-action-button--secondary"
              disabled={state.loading || state.refreshing}
              onClick={triggerRefresh}
            >
              Refresh
            </button>
          </div>
          <p className="cq-admin-page__meta">
            {state.refreshing ? 'Refreshing...' : `Last updated ${formatDateTime(state.lastUpdatedAt)}`}
          </p>
        </div>
      </header>

      {state.loading ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Loading system status...</p>
        </div>
      ) : null}

      {!state.loading && state.error ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">Unable to load system status.</p>
          <p className="cq-admin-empty-state__copy">{state.error}</p>
        </div>
      ) : null}

      {!state.loading && !state.error && !internalEntries.length && !platformEntries.length ? (
        <div className="cq-admin-empty-state">
          <p className="cq-admin-empty-state__title">No status data available.</p>
        </div>
      ) : null}

      {!state.loading && !state.error && internalEntries.length ? (
        <section className="cq-admin-ops-section">
          <div className="cq-admin-ops-section__header">
            <h3 className="cq-admin-section-title">Internal System Health</h3>
          </div>
          <div className="cq-admin-ops-grid cq-admin-ops-grid--internal">
            {internalEntries.map((item) => (
              <InternalStatusCard key={item.name} item={item} />
            ))}
          </div>
        </section>
      ) : null}

      {!state.loading && !state.error && platformEntries.length ? (
        <section className="cq-admin-ops-section">
          <div className="cq-admin-ops-section__header">
            <h3 className="cq-admin-section-title">External Platform Status</h3>
          </div>
          <div className="cq-admin-ops-grid cq-admin-ops-grid--platforms">
            {platformEntries.map(([key, item]) => (
              <PlatformStatusCard key={key} name={key.charAt(0).toUpperCase() + key.slice(1)} item={item} />
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
};

export default AdminOpsPage;
